import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Cookie } from "lucide-react";
import { SITE } from "@/lib/site";
import { OPEN_SETTINGS_EVENT } from "@/lib/clarity";
import { Footer } from "@/components/Footer";

const UPDATED = "juni 2026";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display font-black text-[#0B132B] text-xl md:text-2xl tracking-tight">{title}</h2>
    <div className="font-body text-[#4B5563] mt-3 space-y-3 leading-relaxed text-sm md:text-base">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Integritetspolicy – Swedcro Måleri & Fasad";
  }, []);

  return (
    <div data-testid="privacy-page" className="font-body bg-white min-h-screen flex flex-col">
      <header className="bg-[#0B132B] border-b-2 border-[#D92525]">
        <div className="max-w-4xl mx-auto px-5 md:px-10 py-5 flex items-center justify-between gap-4">
          <Link to="/" data-testid="privacy-logo-link" className="inline-flex flex-col items-center leading-none">
            <span className="font-display font-black tracking-tight text-white text-3xl">
              SWED<span className="text-[#D92525]">CRO</span>
            </span>
            <span className="block w-full text-center font-body text-[11px] tracking-[0.25em] uppercase text-white/75 mt-1">
              Måleri &amp; Fasad
            </span>
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 font-display font-bold text-xs tracking-[0.15em] uppercase text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft size={16} /> Till startsidan
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto w-full px-5 md:px-10 py-14 md:py-20">
        <div className="font-display text-xs uppercase tracking-[0.25em] text-[#D92525]">Integritet &amp; cookies</div>
        <h1 className="font-display font-black text-[#0B132B] text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mt-3">
          Integritetspolicy
        </h1>
        <p className="font-body text-[#4B5563] mt-5 max-w-2xl leading-relaxed">
          Här beskriver vi hur Swedcro Måleri &amp; Fasad behandlar personuppgifter och använder cookies på swedcro.se.
          Senast uppdaterad: {UPDATED}.
        </p>

        <Section title="Personuppgiftsansvarig">
          <p>
            Swedcro Måleri &amp; Fasad, Eskilstuna, är ansvarig för behandlingen av dina personuppgifter på denna webbplats.
            Kontakt: <a href={`mailto:${SITE.email}`} className="text-[#D92525] hover:underline">{SITE.email}</a>,{" "}
            <a href={SITE.phoneHref} className="text-[#D92525] hover:underline">{SITE.phone}</a>.
          </p>
        </Section>

        <Section title="Uppgifter du lämnar till oss">
          <p>
            När du skickar ett meddelande via kontaktformuläret eller bokar en kostnadsfri besiktning behandlar vi de
            uppgifter du anger (t.ex. namn, telefonnummer, e-postadress och din beskrivning av projektet). Uppgifterna
            används enbart för att besvara din förfrågan, lämna offert och planera uppdraget. Rättslig grund är vårt
            berättigade intresse av att besvara förfrågningar samt, vid avtal, fullgörande av avtal.
          </p>
          <p>Uppgifterna sparas så länge det behövs för ändamålet och raderas därefter, om inte bokföringslagen kräver längre lagring.</p>
        </Section>

        <Section title="Cookies">
          <p>
            <strong className="text-[#0B132B]">Nödvändiga cookies</strong> krävs för att webbplatsen ska fungera, till exempel för
            att komma ihåg ditt cookieval. Dessa kräver inget samtycke.
          </p>
          <p>
            <strong className="text-[#0B132B]">Statistikcookies</strong> används endast om du har lämnat ditt samtycke i cookie-rutan.
            De hjälper oss att förstå hur webbplatsen används så att vi kan förbättra den.
          </p>
        </Section>

        <Section title="Microsoft Clarity">
          <p>
            Med ditt samtycke använder vi analysverktyget Microsoft Clarity (Microsoft Ireland Operations Ltd). Clarity
            samlar in information om hur du använder webbplatsen – exempelvis klick, scrollning, musrörelser och vilka
            sidor och tjänster som besöks – och sammanställer detta i statistik, värmekartor och sessionsinspelningar.
            Textfält och känsligt innehåll maskeras. Vi använder informationen för att förbättra webbplatsen och förstå
            vilka tjänster som intresserar våra besökare mest.
          </p>
          <p>
            Clarity sätter cookies som <code className="text-xs bg-[#F3F4F6] px-1 py-0.5">_clck</code> och{" "}
            <code className="text-xs bg-[#F3F4F6] px-1 py-0.5">_clsk</code> för att känna igen besökaren under och mellan
            sessioner (lagringstid upp till 12 månader respektive 1 dag). Uppgifterna kan överföras till Microsoft i USA
            med stöd av EU-kommissionens standardavtalsklausuler. Mer information finns i{" "}
            <a
              href="https://privacy.microsoft.com/sv-se/privacystatement"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#D92525] hover:underline"
            >
              Microsofts sekretesspolicy
            </a>
            .
          </p>
          <p>Vi använder inte Clarity för marknadsföring eller annonsering, och vi delar inte dina uppgifter med andra tredje parter.</p>
        </Section>

        <Section title="Ditt samtycke och dina val">
          <p>
            Statistikcookies och Microsoft Clarity aktiveras först när du väljer "Acceptera statistik" i cookie-rutan.
            Väljer du "Endast nödvändiga" sker ingen statistikinsamling. Du kan när som helst ändra eller återkalla ditt
            samtycke – då tas Clarity bort och dess cookies slutar användas.
          </p>
          <button
            type="button"
            data-testid="privacy-cookie-settings"
            onClick={() => window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT))}
            className="inline-flex items-center gap-2 bg-[#0B132B] border-2 border-[#0B132B] text-white font-display font-bold text-xs tracking-[0.15em] uppercase px-5 py-3 hover:bg-white hover:text-[#0B132B] transition-colors"
          >
            <Cookie size={14} /> Ändra cookie-inställningar
          </button>
        </Section>

        <Section title="Dina rättigheter">
          <p>
            Du har rätt att begära tillgång till, rättelse eller radering av dina personuppgifter, att invända mot eller
            begränsa behandlingen samt att få ut dina uppgifter (dataportabilitet). Kontakta oss på{" "}
            <a href={`mailto:${SITE.email}`} className="text-[#D92525] hover:underline">{SITE.email}</a> så hjälper vi dig.
            Du har också rätt att lämna klagomål till Integritetsskyddsmyndigheten (IMY).
          </p>
        </Section>

        <Section title="Ändringar">
          <p>Vi kan uppdatera denna policy vid behov. Den senaste versionen finns alltid på denna sida.</p>
        </Section>
      </main>

      <Footer />
    </div>
  );
}
